/* Shared QT behavior: loaded once by layout/theme.liquid. */
(() => {
  if (window.__queenTapeRuntimeReady) return;
  window.__queenTapeRuntimeReady = true;
  if (!customElements.get('qt-benefit-guide')) {
    customElements.define('qt-benefit-guide', class extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;
        this.onClick = event => {
          const button = event.target.closest('[data-qt-choice]');
          if (button && this.contains(button)) this.show(button.dataset.qtChoice);
        };
        this.onSelect = event => {
          if (!this.contains(event.target)) return;
          const panel = event.target.closest('[data-qt-panel]');
          if (panel) this.show(panel.dataset.qtPanel);
        };
        this.addEventListener('click', this.onClick);
        this.addEventListener('shopify:block:select', this.onSelect);
        const first = this.querySelector('[data-qt-panel]');
        if (first) this.show(first.dataset.qtPanel);
      }
      show(id) {
        this.querySelectorAll('[data-qt-panel]').forEach(panel => panel.hidden = panel.dataset.qtPanel !== id);
        this.querySelectorAll('[data-qt-choice]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.qtChoice === id)));
      }
      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('shopify:block:select', this.onSelect);
        this.ready = false;
      }
    });
  }
  if (!customElements.get('qt-content-slider')) {
    customElements.define('qt-content-slider', class extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.track = this.querySelector('[data-qt-track]');
        if (!this.track) return;
        this.ready = true;
        this.slides = Array.from(this.track.children);
        this.dots = Array.from(this.querySelectorAll('[data-qt-slide]'));
        this.previous = this.querySelector('[data-qt-prev]');
        this.next = this.querySelector('[data-qt-next]');
        this.controls = this.querySelector('.qt-slider-controls');
        this.index = 0;
        this.click = event => {
          const button = event.target.closest('button');
          if (!button || !this.contains(button)) return;
          if (button.hasAttribute('data-qt-slide')) this.go(Number(button.dataset.qtSlide));
          else if (button.hasAttribute('data-qt-next')) this.go(this.index + 1);
          else if (button.hasAttribute('data-qt-prev')) this.go(this.index - 1);
        };
        this.select = event => {
          const index = this.slides.findIndex(slide => slide === event.target || slide.contains(event.target));
          if (index >= 0) this.go(index);
        };
        this.scheduleUpdate = () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
        };
        this.addEventListener('click', this.click);
        this.addEventListener('shopify:block:select', this.select);
        this.track.addEventListener('scroll', this.scheduleUpdate, { passive: true });
        this.resize = new ResizeObserver(this.scheduleUpdate);
        this.resize.observe(this.track);
        this.scheduleUpdate();
      }
      update() {
        if (!this.slides.length) { if (this.controls) this.controls.hidden = true; return; }
        // Read geometry once before changing any attributes or styles.
        const left = this.track.getBoundingClientRect().left;
        const positions = this.slides.map(slide => slide.getBoundingClientRect().left - left);
        const scroll = this.track.scrollLeft;
        const width = this.track.clientWidth;
        const total = this.track.scrollWidth;
        this.index = positions.reduce((best, pos, index) => Math.abs(pos) < Math.abs(positions[best]) ? index : best, 0);
        if (this.previous) this.previous.disabled = scroll < 3;
        if (this.next) this.next.disabled = scroll + width >= total - 3;
        this.dots.forEach((dot, index) => dot.setAttribute('aria-pressed', String(index === this.index)));
        if (this.controls) this.controls.hidden = total <= width + 3;
      }
      go(index) {
        const slide = this.slides[Math.max(0, Math.min(index, this.slides.length - 1))];
        if (!slide) return;
        const left = this.track.scrollLeft + slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
        this.track.scrollTo({ left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }
      disconnectedCallback() {
        this.removeEventListener('click', this.click);
        this.removeEventListener('shopify:block:select', this.select);
        this.track?.removeEventListener('scroll', this.scheduleUpdate);
        this.resize?.disconnect();
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = null;
        this.ready = false;
      }
    });
  }
  // Videos in QT sections play muted on a loop, all the time: they start as their section
  // comes near the screen (including slides off to the side in a slider) and are never paused
  // when scrolled away, so there's no restart or black frame. No controls are shown.
  const observed = new Set();
  const playVideo = video => {
    video.muted = true;
    const p = video.play();
    if (p && p.catch) p.catch(() => {});
  };
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) entry.target.querySelectorAll('.qt-autoplay-video').forEach(playVideo);
    });
  }, { rootMargin: '100% 0px 100% 0px' }) : null;
  const initVideos = root => root.querySelectorAll('.qt-autoplay-video').forEach(video => {
    if (observed.has(video)) return;
    observed.add(video);
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.controls = false;
    video.removeAttribute('controls');
    if (video.preload === 'none') video.preload = 'metadata';
    const area = video.closest('[data-qt-section]') || video.closest('.shopify-section') || video;
    if (observer) observer.observe(area);
    else playVideo(video);
  });
  initVideos(document);
  document.addEventListener('shopify:section:load', event => initVideos(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('.qt-autoplay-video').forEach(video => {
      observed.delete(video);
      video.pause();
    });
  });
})();

// The collection may be inserted on any page by the theme editor.
if (!customElements.get('qt-home-collection')) {
  customElements.define('qt-home-collection', class extends HTMLElement {
    connectedCallback() {
      if (this.onFilter) return;
      const filters = this.querySelector('.qt-filters');
      if (!filters) return;
      filters.hidden = false;
      this.onFilter = event => {
        const button = event.target.closest('[data-qt-filter]');
        if (!button || !filters.contains(button)) return;
        let count = 0;
        this.querySelectorAll('[data-qt-category]').forEach(card => {
          card.hidden = button.dataset.qtFilter !== 'all' && card.dataset.qtCategory !== button.dataset.qtFilter;
          if (!card.hidden) count++;
        });
        filters.querySelectorAll('button').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
        const empty = this.querySelector('.qt-empty');
        if (empty) empty.hidden = count > 0;
      };
      this.addEventListener('click', this.onFilter);
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onFilter);
      this.onFilter = null;
    }
  });
}
